/**
 * The viewers strip and the "is an agent watching" sentences, as pure
 * functions, plus the one piece of per-tab state the heartbeat needs.
 * TASK-041 Phase 2.
 *
 * "Watching" has one meaning here and it is the server's: an agent is in the
 * list exactly while it has a `wait` parked on the document, so a comment now
 * reaches it within seconds. A busy agent that is not parked is not in the
 * list at all, and nothing here guesses otherwise.
 */

import type { Viewer } from './types'

/** Avatars drawn before the rest collapse into `+n`. */
export const STRIP_MAX = 5

/**
 * The strip's order: agents first -- whether one is watching is the thing a
 * reader came to the strip for, and must never be folded into `+n` -- then the
 * reader, then everyone else in the server's order. At most `max` avatars, and
 * a count of the rest.
 */
export function stripView(viewers: Viewer[], max = STRIP_MAX): { shown: Viewer[]; more: number } {
  const rank = (v: Viewer) => (v.kind === 'agent' ? 0 : v.you ? 1 : 2)
  const ordered = viewers
    .map((v, i) => ({ v, i }))
    .sort((a, b) => rank(a.v) - rank(b.v) || a.i - b.i)
    .map(({ v }) => v)
  if (ordered.length <= max) return { shown: ordered, more: 0 }
  return { shown: ordered.slice(0, max), more: ordered.length - max }
}

/** An avatar's tooltip and accessible name: "Claude, for Euan Jardine", "Euan Jardine (you)". */
export function viewerLabel(v: Viewer): string {
  const base = v.kind === 'agent' && v.actingFor ? `${v.name}, for ${v.actingFor}` : v.name
  return v.you ? `${base} (you)` : base
}

/** The agents watching: those parked on the document now. */
export function watchers(viewers: Viewer[] | null | undefined): Viewer[] {
  return (viewers ?? []).filter((v) => v.kind === 'agent')
}

/** Names, readably: "Claude", "Claude and Cursor", "Claude, Cursor and Zed". */
function nameList(names: string[]): string {
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/**
 * The cue beside the strip, readable without hovering: "Claude is watching",
 * or nothing at all when no agent is. Two agents of one name are counted
 * rather than listed twice.
 */
export function watchingCue(viewers: Viewer[] | null | undefined): string {
  const agents = watchers(viewers)
  if (!agents.length) return ''
  const names = [...new Set(agents.map((a) => a.name))]
  if (names.length === 1 && agents.length > 1) return `${agents.length} ${names[0]} agents are watching`
  return `${nameList(names)} ${names.length === 1 ? 'is' : 'are'} watching`
}

/**
 * The composer's honest line (§8). `null` is "presence is not known" -- not
 * loaded yet, or not available to this reader -- and says only what is true
 * without it.
 */
export function composerLine(viewers: Viewer[] | null | undefined): string {
  if (viewers == null) return 'An agent working on this doc sees new comments the next time it checks the doc.'
  const agents = watchers(viewers)
  if (!agents.length) {
    return 'No agent is watching this document. Claude will see this next time it works on the doc.'
  }
  const names = [...new Set(agents.map((a) => a.name))]
  const who = names.length === 1 && agents.length > 1 ? `${agents.length} ${names[0]} agents` : nameList(names)
  const verb = names.length === 1 && agents.length === 1 ? 'is' : 'are'
  return `${who} ${verb} watching this document and will see a new comment within seconds.`
}

const TAB_KEY = 'unomena-docs-presence-tab'

/** This page's key when there is no storage to keep one in. */
let unstored: string | null = null

/**
 * This tab's presence key, minted once and kept in `sessionStorage`, so two
 * tabs of one person are two entries and closing one leaves the other (§1
 * *Two replicas* (c)). Without storage it is minted per page load, which only
 * costs a stale entry that fades on its own.
 */
export function tabSessionKey(): string {
  const mint = () =>
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().replace(/-/g, '')
      : Math.random().toString(36).slice(2) + Date.now().toString(36)
  try {
    const have = sessionStorage.getItem(TAB_KEY)
    if (have && /^[A-Za-z0-9_-]{8,64}$/.test(have)) return have
    const key = mint()
    sessionStorage.setItem(TAB_KEY, key)
    return key
  } catch {
    unstored ??= mint()
    return unstored
  }
}
